import type { InvoicePart } from '../types/invoice';

/** Exact NEX description required for US sales-tax-as-line-item dist rows. */
export const NEX_SALES_TAX_DIST_DESCRIPTION = 'Sales Tax';

const MONEY_EPSILON = 0.005;

export function isSalesTaxLineItemPart(
    part: Pick<InvoicePart, 'salesTaxLineItem' | 'title'> | null | undefined,
): boolean {
    if (!part) return false;
    if (part.salesTaxLineItem === true) return true;
    return String(part.title ?? '').trim().toLowerCase() === 'sales tax';
}

export function buildSalesTaxLineItem(taxesTotal: number): InvoicePart {
    const amount = Math.round((taxesTotal || 0) * 100) / 100;
    return {
        title: NEX_SALES_TAX_DIST_DESCRIPTION,
        partNumber: 'sales-tax',
        qty: 1,
        listPrice: 0,
        unitBuyPrice: amount,
        totalBuyPrice: amount,
        confidence: 1,
        salesTaxLineItem: true,
    };
}

export function stripSalesTaxLineItems(lineItems: InvoicePart[]): {
    lineItems: InvoicePart[];
    strippedTaxTotal: number;
} {
    let strippedTaxTotal = 0;
    const next: InvoicePart[] = [];
    for (const part of lineItems ?? []) {
        if (isSalesTaxLineItemPart(part)) {
            const amount =
                typeof part.totalBuyPrice === 'number' && Number.isFinite(part.totalBuyPrice)
                    ? part.totalBuyPrice
                    : typeof part.unitBuyPrice === 'number' && Number.isFinite(part.unitBuyPrice)
                      ? part.unitBuyPrice
                      : 0;
            strippedTaxTotal += amount;
            continue;
        }
        next.push(part);
    }
    return {
        lineItems: next,
        strippedTaxTotal: Math.round(strippedTaxTotal * 100) / 100,
    };
}

/**
 * When org setting is on (US / single tax only), move header `taxesTotal` onto a line-item row.
 */
export function materializeSalesTaxAsLineItem(args: {
    lineItems: InvoicePart[];
    taxesTotal: number;
    salesTaxAsLineItem?: boolean;
    nexTaxFormat?: 'single' | 'split';
}): { lineItems: InvoicePart[]; taxesTotal: number; materialized: boolean } {
    const { salesTaxAsLineItem, nexTaxFormat } = args;
    if (!salesTaxAsLineItem || nexTaxFormat === 'split') {
        return {
            lineItems: args.lineItems ?? [],
            taxesTotal: typeof args.taxesTotal === 'number' ? args.taxesTotal : 0,
            materialized: false,
        };
    }

    const stripped = stripSalesTaxLineItems(args.lineItems ?? []);
    let taxesTotal =
        (typeof args.taxesTotal === 'number' && Number.isFinite(args.taxesTotal) ? args.taxesTotal : 0) +
        stripped.strippedTaxTotal;
    taxesTotal = Math.round(taxesTotal * 100) / 100;
    let lineItems = stripped.lineItems;

    if (Math.abs(taxesTotal) < MONEY_EPSILON) {
        return { lineItems, taxesTotal: 0, materialized: stripped.strippedTaxTotal !== 0 };
    }

    lineItems = [...lineItems, buildSalesTaxLineItem(taxesTotal)];
    return { lineItems, taxesTotal: 0, materialized: true };
}

export function shouldSynthesizeSalesTaxApDistFromHeader(args: {
    salesTaxAsLineItem?: boolean;
    nexTaxFormat?: 'single' | 'split';
    taxesTotal: number;
    lineItems: InvoicePart[];
}): boolean {
    if (!args.salesTaxAsLineItem || args.nexTaxFormat === 'split') return false;
    if ((args.lineItems ?? []).some((p) => isSalesTaxLineItemPart(p))) return false;
    return Math.abs(args.taxesTotal || 0) >= MONEY_EPSILON;
}
